import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import * as d3 from 'd3';
import ChartContainer from '../common/ChartContainer';
import { CHART_TOOLTIP_CLASS } from '../common/chartTooltipClasses';
import PowerAnalysisControls from './PowerAnalysisControls';
import PlayerControls from '../common/PlayerControls';
import { usePowerAnalysisData } from '../../../../hooks/usePowerAnalysisData';
import { useErrorTracking } from '../../../../hooks/useErrorTracking';
import { useTheme } from '../../../../contexts/ThemeContext';
import type { GraphComponentProps } from '../../../../platform/graph-registry';
import { LeverMode, setLeverMode } from '../../../../config/experiments';
import {
  type PowerDataPoint,
  calculateResponsiveFontSizes,
  calculateFirstStrokeStartTime,
  calculateAllStrokeCycles,
  calculateEffectiveTimeMin,
  calculateWindowBounds,
  calculateWindowRangeLabel,
} from './utils';
import { useQuery } from '@tanstack/react-query';
import { PowerAnalysisChartRenderer, type RenderConfig } from './renderer';
import { getRowerColorByKey } from '../../../../utils/rowerColors';
import telemetryService from '../../../../services/telemetry/telemetryService';
import {
  hullSetFilterSubtitle,
  useHullStabilityFilterOptional,
} from '../../../../contexts/HullStabilityFilterContext';

// Animation configuration
const DEFAULT_ANIMATION_CONFIG = {
  // Target duration to animate through entire session (ms)
  // At 1x speed: show ~30 seconds of data over 30 seconds of wall time (real-time)
  // This creates a realistic visualization pace
  basePlaybackDuration: 30000, // 30 seconds for full session
  updateInterval: 50, // Update every 50ms for smooth animation (20 FPS)
};

// Custom hook for ResizeObserver
const useResizeObserver = (
  ref: React.RefObject<HTMLDivElement>,
  activeTab?: 'power' | 'technique' | 'gps' | 'crew',
  elementKey?: number
) => {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    // Helper to check if element is ready (exists, visible, has dimensions)
    const isElementReady = (el: HTMLElement | null): boolean => {
      if (!el) return false;

      // Check visibility
      let current: HTMLElement | null = el;
      while (current) {
        const style = window.getComputedStyle(current);
        if (style.display === 'none') {
          return false;
        }
        current = current.parentElement;
      }

      // Check dimensions
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    };

    // Setup observer - only called when element is ready
    let animationFrame: number | null = null;
    let observerCleanup: (() => void) | null = null;

    const setupObserver = (currentElement: HTMLElement) => {
      const commitSize = (width: number, height: number) => {
        setDimensions((prev) => {
          if (
            (width === 0 && height === 0) ||
            (prev.width === Math.round(width) && prev.height === Math.round(height))
          ) {
            return prev;
          }
          return { width: Math.round(width), height: Math.round(height) };
        });
      };

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        const { width, height } = entry.contentRect;
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
        animationFrame = requestAnimationFrame(() => commitSize(width, height));
      });

      observer.observe(currentElement);

      const rect = currentElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        commitSize(rect.width, rect.height);
      }

      observerCleanup = () => {
        observer.disconnect();
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    };

    // Check readiness and set up observer
    let retryCount = 0;
    let pendingFrame: number | null = null;
    let isTimeout = false; // Track if pendingFrame is setTimeout or requestAnimationFrame
    const MAX_RETRIES = 20;

    const trySetupObserver = () => {
      const currentElement = ref.current;

      if (isElementReady(currentElement)) {
        // Element is ready - set up observer
        setupObserver(currentElement!);
      } else {
        // Element not ready yet - retry regardless of tab.
        // Starter dashboard uses the power tab and can otherwise fall back to default dimensions,
        // which causes temporary SVG scaling artifacts (font deformation) during resize.
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          // If element doesn't exist, use longer delay (element is created during render)
          // If element exists but not ready, use shorter delay (just waiting for visibility/dimensions)
          if (!currentElement) {
            // Element doesn't exist - wait longer for render to complete
            pendingFrame = setTimeout(trySetupObserver, 50) as unknown as number;
            isTimeout = true;
          } else {
            // Element exists but not ready - check again soon
            pendingFrame = requestAnimationFrame(trySetupObserver);
            isTimeout = false;
          }
        }
      }
    };

    // Start checking readiness
    trySetupObserver();

    // Cleanup function
    return () => {
      if (pendingFrame !== null) {
        if (isTimeout) {
          clearTimeout(pendingFrame);
        } else {
          cancelAnimationFrame(pendingFrame);
        }
        pendingFrame = null;
      }
      if (observerCleanup) {
        observerCleanup();
        observerCleanup = null;
      }
    };
  }, [ref, activeTab, elementKey]); // Re-run when activeTab changes or elementKey changes (element appears)

  return dimensions;
};

interface PowerAnalysisD3Props extends GraphComponentProps {
  title?: string;
  subtitle?: string;
  hideDataSourceToggle?: boolean;
  activeTab?: 'power' | 'technique' | 'gps' | 'crew';
}

const PowerAnalysisD3: React.FC<PowerAnalysisD3Props> = ({
  sessionId,
  parameterInfo,
  className,
  rowerContext,
  title = 'Power Analysis',
  subtitle = 'Real-time waveform visualization',
  hideDataSourceToggle = false,
  activeTab,
}) => {
  const hullFilter = useHullStabilityFilterOptional();
  const outerRef = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef(false);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const zoomTransformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);
  /** When user has zoomed/panned (not playing), stores the visible window so pan doesn't snap back. */
  const lockedWindowRef = useRef<{ windowStart: number; windowEnd: number } | null>(null);
  /** When playing, use this duration centered on currentTime so zoom level is preserved. */
  const lockedWindowDurationRef = useRef<number | null>(null);
  /** Counter incremented when user zooms/pans - triggers transform reset without interfering with playback. */
  const userZoomCounterRef = useRef<number>(0);

  const [dataMode, setDataMode] = useState<'periodic' | 'aperiodic'>('periodic');
  const [leverMode, setLeverModeState] = useState<LeverMode>('gate');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Animation state
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedRowers, setSelectedRowers] = useState<string[]>([]);
  const [speed, setSpeed] = useState(1);
  const [selectedStrokeIndex, setSelectedStrokeIndex] = useState<number | null>(null);
  const [perStrokeMode, setPerStrokeMode] = useState<boolean>(true); // Default: ON
  const [fontsReady, setFontsReady] = useState<boolean>(false);
  /** Bumped on reset to force the render effect to re-run (D3 zoom mutates SVG outside React) */
  const [renderVersion, setRenderVersion] = useState(0);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const prevDimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  // Refs to avoid stale closures in keyboard event handler
  const selectedStrokeIndexRef = useRef<number | null>(null);
  const perStrokeModeRef = useRef<boolean>(true);
  const allStrokeCyclesRef = useRef<Array<{ start: number; end: number; strokeNumber: number }>>(
    []
  );
  const timeExtentRef = useRef<[number, number]>([0, 0]);
  const animationRangeRef = useRef<number>(1);
  const progressRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);
  /** Track current visible window coordinates (updated during render) for reliable access in callbacks */
  const currentWindowRef = useRef<{ windowStart: number; windowEnd: number }>({
    windowStart: 0,
    windowEnd: 1,
  });
  /** When starting play, seed the animation from this index (avoids first-tick jump to 0 before state commits). */
  const playStartIndexRef = useRef<number | null>(null);
  /** Set when starting play so render effect can defer one frame and avoid UI freeze. */
  const startingPlayRef = useRef<boolean>(false);

  // Keep refs in sync with state for reliable reads in event handlers
  useEffect(() => {
    selectedStrokeIndexRef.current = selectedStrokeIndex;
  }, [selectedStrokeIndex]);

  useEffect(() => {
    perStrokeModeRef.current = perStrokeMode;
  }, [perStrokeMode]);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  const { reportError } = useErrorTracking('PowerAnalysisD3');
  const { resolved: resolvedTheme } = useTheme();

  const handleLegendToggle = useCallback((key: string) => {
    setSelectedRowers((prev) => {
      if (prev.length === 0) {
        // No selection active — solo this rower
        return [key];
      }
      if (prev.includes(key)) {
        // Already selected — deselect (if last one, return to all-visible)
        return prev.filter((k) => k !== key);
      }
      // Add to selection
      return [...prev, key];
    });
  }, []);

  // Fetch data using existing hook
  const {
    correctedData,
    loading,
    error,
    alpha,
    method,
    hasCalibration,
    calibrationReady,
    strokeBounds,
    refetchCalibration,
  } = usePowerAnalysisData({
    sessionId: sessionId || '',
    parameterInfo,
    dataMode,
    leverMode,
  });

  // Session seat_id → name mapping for rower display labels (cached, shared with IRP if loaded)
  const { data: seatIdMapping } = useQuery({
    queryKey: ['seat-id-mapping', sessionId],
    queryFn: () => telemetryService.getSeatIdMapping(sessionId || ''),
    enabled: !!sessionId,
    staleTime: 5 * 60 * 1000,
  });
  const nameMapping: Record<string | number, string> | null =
    (seatIdMapping as any)?.__nameMapping || null;

  // Lever suggestion (TODO: implement calculation like PowerAnalysisChart.tsx:137-180)
  const leverSuggestion = null;

  // Extract rower keys
  const rowerKeys = useMemo(() => {
    if (!correctedData || correctedData.length === 0) return [];
    return Object.keys(correctedData[0]).filter((key) => key !== 'time');
  }, [correctedData]);

  // Filter rowers based on rowerContext mode (compliance: rowers should only see their own data)
  // If rowerContext.mode === 'filter', only show that rower's data (like Chart.js version)
  // If rowerContext.mode === 'highlight' or no context, show all rowers (coach view)
  const visibleRowers = useMemo(() => {
    // If filter mode, only include the focused rower
    if (rowerContext && rowerContext.mode === 'filter') {
      const focusedRowerKey = `Rower ${rowerContext.position}`;
      return rowerKeys.filter((key) => key === focusedRowerKey);
    }
    // Otherwise show all rowers (will be highlighted/grayscaled for coach view)
    return rowerKeys;
  }, [rowerKeys, rowerContext]);

  // Inclusion model: when selectedRowers is non-empty, everything NOT selected is hidden
  const hiddenRowerSet = useMemo(() => {
    if (selectedRowers.length === 0) return new Set<string>();
    return new Set(visibleRowers.filter((key) => !selectedRowers.includes(key)));
  }, [selectedRowers, visibleRowers]);

  // Determine which rower is focused (for rower journey mode)
  const focusedRowerKey = useMemo(() => {
    if (!rowerContext) return null;
    const rowerKey = `Rower ${rowerContext.position}`;
    return rowerKeys.includes(rowerKey) ? rowerKey : null;
  }, [rowerKeys, rowerContext]);

  const activeRowerKeys = useMemo(() => {
    const filtered = visibleRowers.filter((key) => !hiddenRowerSet.has(key));
    return filtered.length > 0 ? filtered : visibleRowers;
  }, [visibleRowers, hiddenRowerSet]);

  const timeExtent = useMemo<[number, number]>(() => {
    if (!correctedData || correctedData.length === 0) return [0, 1];
    return d3.extent(correctedData, (d) => (d as PowerDataPoint).time) as [number, number];
  }, [correctedData]);

  // Find first complete stroke start time (in normalized seconds to match correctedData.time)
  const firstStrokeStartTime = useMemo<number | null>(
    () => calculateFirstStrokeStartTime(correctedData || [], dataMode, strokeBounds, timeExtent),
    [strokeBounds, correctedData, dataMode, timeExtent]
  );

  // Calculate effective minimum time for initial view
  // Use first stroke start if available, otherwise use timeExtent[0]
  const effectiveTimeMin = useMemo(
    () => calculateEffectiveTimeMin(firstStrokeStartTime, timeExtent),
    [firstStrokeStartTime, timeExtent]
  );

  // Calculate ALL stroke cycles once from the full dataset (not just visible window)
  // This ensures stroke numbers are consistent across pan/zoom
  const allStrokeCycles = useMemo<
    Array<{ start: number; end: number; strokeNumber: number }>
  >(() => {
    const cycles = calculateAllStrokeCycles(correctedData || [], dataMode, strokeBounds);
    if (!hullFilter?.isFilterActive || !hullFilter.allowedStrokeIndices) {
      return cycles;
    }
    return cycles.filter((cycle) => hullFilter.allowedStrokeIndices!.has(cycle.strokeNumber));
  }, [correctedData, dataMode, strokeBounds, hullFilter]);

  // Update refs when values change
  useEffect(() => {
    allStrokeCyclesRef.current = allStrokeCycles;
  }, [allStrokeCycles]);

  useEffect(() => {
    timeExtentRef.current = timeExtent;
  }, [timeExtent]);

  // Keep selectedStrokeIndex in bounds so we never trigger "reset to first stroke" elsewhere
  // Only clamp when NOT playing so we don't interrupt animation mid-cycle
  useEffect(() => {
    if (isPlaying) return; // Don't interrupt animation - let loop control index
    if (allStrokeCycles.length === 0 || !timeExtent) return;
    if (selectedStrokeIndex !== null && selectedStrokeIndex >= allStrokeCycles.length) {
      const lastIndex = allStrokeCycles.length - 1;
      const range = Math.max(timeExtent[1] - timeExtent[0], 1);
      setSelectedStrokeIndex(lastIndex);
      const stroke = allStrokeCycles[lastIndex];
      const strokeCenterTime = (stroke.start + stroke.end) / 2;
      const newProgress = ((strokeCenterTime - timeExtent[0]) / range) * 100;
      setProgress(Math.max(0, Math.min(100, newProgress)));
    }
  }, [allStrokeCycles, selectedStrokeIndex, timeExtent, isPlaying]);

  const hasData = !!correctedData && correctedData.length > 0;

  // Re-run ResizeObserver setup after data arrives and layout settles. Must run on the power
  // tab too (starter dashboard); technique-only was a regression that skipped re-observe on power
  // and left 0×0 / fallback widths → SVG text scaling artifacts (see 457ab090).
  const [elementKey, setElementKey] = useState(0);
  useEffect(() => {
    if (!hasData) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled || !chartContainerRef.current) return;
        setElementKey((k) => k + 1);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [hasData, activeTab]);

  const observedDimensions = useResizeObserver(chartContainerRef, activeTab, elementKey);
  const width = observedDimensions.width || 800;
  const height = observedDimensions.height || 600;

  // Calculate responsive font sizes based on container width
  // Match the pattern used in PowerAnalysisControls and PowerAnalysisChart
  const responsiveFontSizes = useMemo(() => calculateResponsiveFontSizes(width), [width]);

  const animationRange = Math.max(timeExtent[1] - timeExtent[0], 1);
  /** Default time window in continuous mode: fixed 3s; cap by session length. */
  const windowDuration = Math.min(animationRange, perStrokeMode ? 6 : 3);
  const currentTime = Math.min(timeExtent[1], timeExtent[0] + (progress / 100) * animationRange);

  // Update animationRange ref
  useEffect(() => {
    animationRangeRef.current = animationRange;
  }, [animationRange]);

  // Center window around currentTime, but maintain full width when clamped at edges
  // For initial view (progress === 0), use effectiveTimeMin to start from first complete stroke
  // For animation/scrubbing (progress > 0), use normal timeExtent[0] behavior
  const isInitialView = progress === 0;

  const computedWindow = calculateWindowBounds({
    perStrokeMode,
    isInitialView,
    selectedStrokeIndex,
    allStrokeCycles,
    currentTime,
    timeExtent,
    effectiveTimeMin,
    windowDuration,
  });

  // Window calculation: match behavior when arrow keys worked (fc17c0c7)
  // In per-stroke mode always use computedWindow so arrows (selectedStrokeIndex) drive the view
  let windowStart: number;
  let windowEnd: number;
  let useLockedView = false;

  const lockedDuration =
    lockedWindowDurationRef.current ??
    (lockedWindowRef.current
      ? lockedWindowRef.current.windowEnd - lockedWindowRef.current.windowStart
      : null);

  if (perStrokeMode) {
    // After zoom (shift+drag): keep zoom level; center on selected stroke when set, else preserve exact window (no snap back)
    if (lockedDuration !== null) {
      if (selectedStrokeIndex !== null && allStrokeCycles[selectedStrokeIndex]) {
        const stroke = allStrokeCycles[selectedStrokeIndex];
        const strokeCenter = (stroke.start + stroke.end) / 2;
        const halfDuration = lockedDuration / 2;
        windowStart = Math.max(timeExtent[0], strokeCenter - halfDuration);
        windowEnd = Math.min(timeExtent[1], strokeCenter + halfDuration);
        if (windowStart === timeExtent[0]) {
          windowEnd = Math.min(timeExtent[1], windowStart + lockedDuration);
        } else if (windowEnd === timeExtent[1]) {
          windowStart = Math.max(timeExtent[0], windowEnd - lockedDuration);
        }
        lockedWindowRef.current = { windowStart, windowEnd };
      } else if (lockedWindowRef.current !== null) {
        // Keep exact zoomed window so view does not snap back
        windowStart = lockedWindowRef.current.windowStart;
        windowEnd = lockedWindowRef.current.windowEnd;
      } else {
        windowStart = computedWindow.windowStart;
        windowEnd = computedWindow.windowEnd;
      }
      useLockedView = true;
    } else {
      windowStart = computedWindow.windowStart;
      windowEnd = computedWindow.windowEnd;
    }
  } else if (lockedDuration !== null && isPlaying) {
    // Playing in normal mode with zoom: center on currentTime
    const halfDuration = lockedDuration / 2;
    windowStart = Math.max(timeExtent[0], currentTime - halfDuration);
    windowEnd = Math.min(timeExtent[1], currentTime + halfDuration);
    if (windowStart === timeExtent[0]) {
      windowEnd = Math.min(timeExtent[1], windowStart + lockedDuration);
    } else if (windowEnd === timeExtent[1]) {
      windowStart = Math.max(timeExtent[0], windowEnd - lockedDuration);
    }
    useLockedView = true;
    lockedWindowRef.current = { windowStart, windowEnd };
  } else if (!isPlaying && lockedWindowRef.current !== null && !perStrokeMode) {
    // Not playing, normal mode, user has manually panned: preserve exact window
    windowStart = lockedWindowRef.current.windowStart;
    windowEnd = lockedWindowRef.current.windowEnd;
    useLockedView = true;
  } else {
    // No zoom or normal behavior: use computed window (derived from selectedStrokeIndex in per-stroke mode)
    windowStart = computedWindow.windowStart;
    windowEnd = computedWindow.windowEnd;
  }

  // Always keep currentWindowRef updated with the actual visible window (for reliable access in callbacks)
  currentWindowRef.current = { windowStart, windowEnd };

  const windowRangeLabel = useMemo(
    () => calculateWindowRangeLabel(hasData, windowStart, windowEnd),
    [hasData, windowStart, windowEnd]
  );

  /** Called by the renderer when the user zooms or pans; store window for visual zoom preservation ONLY.
   * NEVER modifies selectedStrokeIndex - that is controlled exclusively by navigation (arrows/play). */
  const handleZoomToWindow = useCallback((wStart: number, wEnd: number) => {
    lockedWindowRef.current = { windowStart: wStart, windowEnd: wEnd };
    lockedWindowDurationRef.current = wEnd - wStart;
    currentWindowRef.current = { windowStart: wStart, windowEnd: wEnd };
    userZoomCounterRef.current += 1;
    // Do NOT touch selectedStrokeIndex - zoom is purely visual
  }, []);

  /** Force the render effect to re-run so React redraws the chart with current window bounds. */
  const handleForceReRender = useCallback(() => {
    setRenderVersion((v) => v + 1);
  }, []);

  // Wait for fonts to load before rendering text to prevent squashed font issue
  useEffect(() => {
    // Check if document.fonts API is available
    if (typeof document !== 'undefined' && 'fonts' in document) {
      // Helper function to check if Inter font is actually loaded
      const checkInterFont = (): boolean => {
        // Check multiple font weights and sizes to ensure Inter is loaded
        // Format: 'size "Font Name"' - quotes are required around font name
        return (
          document.fonts.check('12px "Inter"') &&
          document.fonts.check('14px "Inter"') &&
          document.fonts.check('16px "Inter"')
        );
      };

      // Check if fonts are already loaded and Inter is available
      if (document.fonts.status === 'loaded' && checkInterFont()) {
        setFontsReady(true);
        return;
      }

      // Wait for fonts to load, then verify Inter specifically
      // Use document.fonts.load() to explicitly load Inter and wait for it
      const loadInterFont = async () => {
        try {
          // Explicitly load Inter font with multiple weights to ensure it's ready
          // Format: 'weight size "Font Name"' - quotes are required around font name
          await Promise.all([
            document.fonts.load('400 12px "Inter"'),
            document.fonts.load('500 14px "Inter"'),
            document.fonts.load('500 16px "Inter"'),
          ]);

          // Double-check that Inter is actually available
          if (checkInterFont()) {
            // Use multiple requestAnimationFrame calls to ensure browser has fully applied font metrics
            // This is critical for SVG text which doesn't automatically update after font swap
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Final delay to ensure SVG text elements have updated metrics
                setTimeout(() => {
                  setFontsReady(true);
                }, 150);
              });
            });
          } else {
            // If still not ready, poll with backoff
            let attempts = 0;
            const maxAttempts = 20;
            const pollInterval = 100;

            const pollForFont = () => {
              attempts++;
              if (checkInterFont() || attempts >= maxAttempts) {
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    setTimeout(() => {
                      setFontsReady(true);
                    }, 150);
                  });
                });
              } else {
                setTimeout(pollForFont, pollInterval);
              }
            };

            setTimeout(pollForFont, pollInterval);
          }
        } catch (error) {
          // Fallback: proceed after delay
          setTimeout(() => setFontsReady(true), 500);
        }
      };

      // Start loading fonts
      loadInterFont();
    } else {
      // Fallback for browsers without document.fonts API
      // Wait longer for fonts to load via CSS @import
      setTimeout(() => setFontsReady(true), 500);
    }
  }, []);

  // D3 Chart Rendering
  const rendererRef = useRef<PowerAnalysisChartRenderer | null>(null);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (!svgElement || loading || !hasData || !fontsReady) {
      return;
    }

    // Create or reuse renderer instance
    if (!rendererRef.current) {
      rendererRef.current = new PowerAnalysisChartRenderer();
    }

    const renderConfig: RenderConfig = {
      svgElement,
      containerElement: chartContainerRef.current,
      tooltipElement: tooltipRef.current,
      loading,
      hasData,
      fontsReady,
      width,
      height,
      correctedData: correctedData || [],
      visibleRowers,
      activeRowerKeys,
      sessionId,
      rowerContext,
      focusedRowerKey,
      windowStart,
      windowEnd,
      windowDuration,
      dataMode,
      strokeBounds,
      hiddenRowerSet,
      handleLegendToggle,
      currentTime,
      timeExtent,
      effectiveTimeMin,
      animationRange,
      isPlaying,
      allStrokeCycles,
      responsiveFontSizes,
      perStrokeMode,
      selectedStrokeIndex,
      activeTab,
      prevDimensionsRef,
      zoomRef,
      zoomTransformRef,
      onZoomToWindow: handleZoomToWindow,
      onForceReRender: handleForceReRender,
      useLockedView,
    };

    // When just started play, defer render one frame so play button updates immediately (removes latency after zoom then play)
    if (startingPlayRef.current) {
      startingPlayRef.current = false;
      const rafId = requestAnimationFrame(() => {
        if (rendererRef.current) {
          rendererRef.current.render(renderConfig);
        }
      });
      return () => {
        cancelAnimationFrame(rafId);
        if (rendererRef.current) {
          rendererRef.current.cleanup();
        }
      };
    }
    if (rendererRef.current) {
      rendererRef.current.render(renderConfig);
    }
    return () => {
      if (rendererRef.current) {
        rendererRef.current.cleanup();
      }
    };
  }, [
    correctedData,
    visibleRowers,
    activeRowerKeys,
    width,
    height,
    sessionId,
    rowerContext,
    focusedRowerKey,
    windowStart,
    windowEnd,
    windowDuration,
    hasData,
    dataMode,
    strokeBounds,
    hiddenRowerSet,
    handleLegendToggle,
    currentTime,
    timeExtent,
    effectiveTimeMin,
    animationRange,
    isPlaying,
    loading,
    allStrokeCycles,
    responsiveFontSizes,
    perStrokeMode,
    selectedStrokeIndex,
    fontsReady,
    activeTab,
    handleZoomToWindow,
    handleForceReRender,
    useLockedView,
    renderVersion,
    resolvedTheme,
  ]);

  // Animation loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isPlaying || !correctedData || correctedData.length === 0) return;

    // In per-stroke mode: cycle through strokes (1 second per stroke)
    // In normal mode: animate through time with progress bar
    if (perStrokeMode && allStrokeCycles.length > 0) {
      // Capture stroke list and animation range at animation start - don't let them change mid-animation
      const strokeListSnapshot = [...allStrokeCycles];
      const currentAnimationRange = Math.max(timeExtent[1] - timeExtent[0], 1);

      // Per-stroke mode: cycle through strokes sequentially (use playStartIndexRef so we start from zoomed stroke)
      const strokeInterval = setInterval(() => {
        setSelectedStrokeIndex((prevIndex) => {
          const currentIndex = prevIndex !== null ? prevIndex : (playStartIndexRef.current ?? 0);
          if (playStartIndexRef.current !== null) playStartIndexRef.current = null;
          const nextIndex = currentIndex + 1;

          // Use snapshot length so animation doesn't restart if strokes recalculate
          if (nextIndex >= strokeListSnapshot.length) {
            // Reached end of strokes - stop playing
            setIsPlaying(false);
            return strokeListSnapshot.length - 1; // Stay on last stroke
          }

          // Update progress to center of next stroke for consistency
          const nextStroke = strokeListSnapshot[nextIndex];
          if (nextStroke && timeExtent) {
            const strokeCenterTime = (nextStroke.start + nextStroke.end) / 2;
            const newProgress = ((strokeCenterTime - timeExtent[0]) / currentAnimationRange) * 100;
            setProgress(Math.max(0, Math.min(100, newProgress)));
          }

          return nextIndex;
        });
      }, 1000); // 1 second per stroke

      return () => clearInterval(strokeInterval);
    }

    // Normal mode: animate through time with progress bar
    // Calculate effective playback duration based on actual session length
    // At 1x speed: 2x playback speed (1 second of data = 0.5 seconds of animation)
    // This keeps animations reasonably fast while still scaling with session length
    const animationRange = Math.max(timeExtent[1] - timeExtent[0], 1);
    const effectivePlaybackDuration = (animationRange * 1000) / 2; // 2x speed base

    const interval = setInterval(() => {
      setProgress((prev) => {
        // Calculate progress increment based on:
        // - Speed multiplier (0.5x, 1x, 2x, etc.)
        // - Effective playback duration (scales with session length)
        // - Update interval (50ms)
        // Formula: (updateInterval / effectivePlaybackDuration) * 100 * speed
        // At 1x: real-time playback (60s session = 60s animation, 120s session = 120s animation)
        const increment =
          (DEFAULT_ANIMATION_CONFIG.updateInterval / effectivePlaybackDuration) * 100 * speed;
        const newProgress = prev + increment;
        if (newProgress >= 100) {
          setIsPlaying(false);
          return 100;
        }
        return newProgress;
      });
    }, DEFAULT_ANIMATION_CONFIG.updateInterval);

    return () => clearInterval(interval);
    // Intentionally depend on allStrokeCycles.length only so animation restarts when stroke count changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPlaying,
    speed,
    correctedData,
    timeExtent,
    perStrokeMode,
    allStrokeCycles.length,
    animationRange,
  ]);

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      // Pausing: update locked window so view doesn't snap when we switch from "playing" to "not playing" mode
      if (lockedWindowDurationRef.current !== null && timeExtent) {
        const range = Math.max(timeExtent[1] - timeExtent[0], 1);
        const ct = timeExtent[0] + (progress / 100) * range;
        const half = lockedWindowDurationRef.current / 2;
        let wStart = Math.max(timeExtent[0], ct - half);
        let wEnd = Math.min(timeExtent[1], ct + half);
        if (wStart === timeExtent[0]) {
          wEnd = Math.min(timeExtent[1], wStart + lockedWindowDurationRef.current);
        } else if (wEnd === timeExtent[1]) {
          wStart = Math.max(timeExtent[0], wEnd - lockedWindowDurationRef.current);
        }
        lockedWindowRef.current = { windowStart: wStart, windowEnd: wEnd };
      }
    } else {
      // Starting play: kill D3 zoom transform so first frame is clean
      if (svgRef.current) {
        zoomTransformRef.current = d3.zoomIdentity;
        d3.select(svgRef.current).property('__zoom', d3.zoomIdentity);
      }

      // Start from current view after zoom, not from first stroke
      let startIndex: number;
      const locked = lockedWindowRef.current;
      if (perStrokeMode && allStrokeCycles.length > 0 && locked) {
        const centerTime = (locked.windowStart + locked.windowEnd) / 2;
        const idx = allStrokeCycles.findIndex((s) => s.start <= centerTime && centerTime <= s.end);
        if (idx >= 0) {
          startIndex = idx;
        } else {
          let nearest = 0;
          let best = Infinity;
          allStrokeCycles.forEach((s, i) => {
            const mid = (s.start + s.end) / 2;
            const d = Math.abs(mid - centerTime);
            if (d < best) {
              best = d;
              nearest = i;
            }
          });
          startIndex = nearest;
        }
      } else {
        startIndex =
          selectedStrokeIndex !== null &&
          selectedStrokeIndex >= 0 &&
          selectedStrokeIndex < allStrokeCycles.length
            ? selectedStrokeIndex
            : 0;
      }

      if (perStrokeMode && allStrokeCycles.length > 0 && timeExtent) {
        const stroke = allStrokeCycles[startIndex];
        const range = Math.max(timeExtent[1] - timeExtent[0], 1);
        const strokeCenterTime = (stroke.start + stroke.end) / 2;
        const newProgress = ((strokeCenterTime - timeExtent[0]) / range) * 100;
        setProgress(Math.max(0, Math.min(100, newProgress)));
        setSelectedStrokeIndex(startIndex);
        selectedStrokeIndexRef.current = startIndex;
        playStartIndexRef.current = startIndex; // Animation loop uses this so first tick continues from zoomed stroke
      }
      startingPlayRef.current = true; // Defer next render one frame so play feels instant
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, perStrokeMode, allStrokeCycles, selectedStrokeIndex, timeExtent, progress]);

  const handleReset = useCallback(() => {
    setIsPlaying(false);
    setProgress(0);
    setSelectedRowers([]); // Reset legend selection back to all-visible
    // In per-stroke mode, reset to first stroke. In normal mode, clear selection.
    if (perStrokeMode && allStrokeCycles.length > 0) {
      setSelectedStrokeIndex(0);
    } else {
      setSelectedStrokeIndex(null);
    }
    // Reset zoom transform and locked window refs so view follows progress/selected stroke again
    lockedWindowRef.current = null;
    lockedWindowDurationRef.current = null;
    userZoomCounterRef.current = 0;
    if (svgRef.current && zoomRef.current) {
      zoomTransformRef.current = d3.zoomIdentity;
      const svgSel = d3.select(svgRef.current);
      svgSel.interrupt();
      svgSel.property('__zoom', d3.zoomIdentity);
    }
    // Force render effect to re-run (D3 zoom mutates SVG outside React lifecycle)
    setRenderVersion((v) => v + 1);
  }, [perStrokeMode, allStrokeCycles.length]);

  const handleSpeedChange = (newSpeed: number) => {
    setSpeed(newSpeed);
  };

  const handleProgressChange = useCallback(
    (newProgress: number, clearSelectedStroke: boolean = true) => {
      setIsPlaying(false);
      setProgress(newProgress);

      // In per-stroke mode, update selectedStrokeIndex based on current time (for dragging)
      // Always find the nearest stroke, even if time is between strokes
      // In normal mode, clear selectedStrokeIndex
      if (clearSelectedStroke) {
        if (perStrokeMode && allStrokeCycles.length > 0 && timeExtent && animationRange) {
          const newCurrentTime = timeExtent[0] + (newProgress / 100) * animationRange;

          // First try to find stroke that contains this time
          let strokeAtTime = allStrokeCycles.find(
            (stroke) => stroke.start <= newCurrentTime && newCurrentTime <= stroke.end
          );

          // If no stroke contains the time (between strokes), find the nearest stroke
          if (!strokeAtTime) {
            let minDistance = Infinity;
            let nearestStroke: (typeof allStrokeCycles)[0] | null = null;

            for (const stroke of allStrokeCycles) {
              const strokeCenter = (stroke.start + stroke.end) / 2;
              const distance = Math.abs(newCurrentTime - strokeCenter);
              if (distance < minDistance) {
                minDistance = distance;
                nearestStroke = stroke;
              }
            }

            strokeAtTime = nearestStroke;
          }

          if (strokeAtTime) {
            const strokeIndex = allStrokeCycles.findIndex(
              (s) => s.strokeNumber === strokeAtTime!.strokeNumber
            );
            setSelectedStrokeIndex(strokeIndex !== -1 ? strokeIndex : null);
          } else {
            // Fallback: keep current selectedStrokeIndex or use first stroke
            if (selectedStrokeIndex === null && allStrokeCycles.length > 0) {
              setSelectedStrokeIndex(0);
            }
          }
        } else {
          // Normal mode: clear selected stroke
          setSelectedStrokeIndex(null);
        }
      }
    },
    [perStrokeMode, allStrokeCycles, timeExtent, animationRange, selectedStrokeIndex]
  );

  const handleStrokeClick = useCallback(
    (strokeNumber: number) => {
      // Only handle stroke click in per-stroke mode
      if (!perStrokeMode) return;

      // Find stroke index from stroke number
      const strokeIndex = allStrokeCycles.findIndex(
        (stroke) => stroke.strokeNumber === strokeNumber
      );
      if (strokeIndex !== -1) {
        setIsPlaying(false);
        // Set both state updates together to avoid glitch
        // Use functional updates to ensure they happen in the same render
        const stroke = allStrokeCycles[strokeIndex];
        const strokeCenterTime = (stroke.start + stroke.end) / 2;
        const newProgress = ((strokeCenterTime - timeExtent[0]) / animationRange) * 100;

        // Set selectedStrokeIndex first, then progress
        // This ensures window calculation uses selectedStrokeIndex immediately
        setSelectedStrokeIndex(strokeIndex);
        setProgress(Math.max(0, Math.min(100, newProgress)));
      }
    },
    [allStrokeCycles, timeExtent, animationRange, perStrokeMode]
  );

  const _handleDataModeToggle = () => {
    setDataMode((prev) => (prev === 'periodic' ? 'aperiodic' : 'periodic'));
  };

  const handleLeverModeToggle = () => {
    const newMode: LeverMode = leverMode === 'gate' ? 'handle' : 'gate';
    setLeverMode(newMode);
    setLeverModeState(newMode);
  };

  const handleShowAdvancedToggle = () => {
    setShowAdvanced((prev) => !prev);
  };

  const handleCalibrationComplete = useCallback(async () => {
    await refetchCalibration();
    setRenderVersion((v) => v + 1);
  }, [refetchCalibration]);

  const handleCalibrationRemoved = useCallback(async () => {
    await refetchCalibration();
    setRenderVersion((v) => v + 1);
  }, [refetchCalibration]);

  const handleResetZoom = () => {
    lockedWindowRef.current = null;
    lockedWindowDurationRef.current = null;
    userZoomCounterRef.current = 0;
    if (svgRef.current && zoomRef.current) {
      zoomTransformRef.current = d3.zoomIdentity;
      const svgSel = d3.select(svgRef.current);
      svgSel.interrupt();
      svgSel.property('__zoom', d3.zoomIdentity);
    }
    // Force render effect to re-run (D3 zoom mutates SVG outside React lifecycle)
    setRenderVersion((v) => v + 1);
  };

  // Keyboard shortcuts: Space = play/pause, R = reset, Left/Right = previous/next stroke or nudge time
  // Use index-based navigation for Left/Right so we never wrap or jump (e.g. after stroke 5 go to 6, not 1)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isHoveredRef.current) return;
      const target = e.target as HTMLElement;
      const isEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable;
      if (isEditable) return;

      if (e.code === 'Space') {
        e.preventDefault();
        if (hasData) handlePlayPause();
        return;
      }
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        if (hasData) handleReset();
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const currentPerStrokeMode = perStrokeModeRef.current;
        const strokes = allStrokeCyclesRef.current;
        const currentIndex = selectedStrokeIndexRef.current ?? 0;
        const prevIndex = currentIndex - 1;
        const inBounds = prevIndex >= 0;
        // Invariant: arrow → index ±1 → view snaps. Only block on: no data, or bounds.
        const shouldAdvance = hasData && currentPerStrokeMode && strokes.length > 0 && inBounds;
        if (!hasData) return;
        // Per-stroke mode only: move by one stroke; no action in normal mode
        if (shouldAdvance) {
          const stroke = strokes[prevIndex];
          const strokeCenterTime = (stroke.start + stroke.end) / 2;
          const extent = timeExtentRef.current;
          const range = animationRangeRef.current;
          const t0 = extent?.[0] ?? strokes[0]?.start ?? 0;
          const animRange =
            range ?? (strokes.length > 0 ? strokes[strokes.length - 1].end - t0 : 1);
          const newProgress = Math.max(
            0,
            Math.min(100, ((strokeCenterTime - t0) / animRange) * 100)
          );
          setIsPlaying(false);
          setSelectedStrokeIndex(prevIndex);
          selectedStrokeIndexRef.current = prevIndex;
          setProgress(newProgress);
        }
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const currentPerStrokeMode = perStrokeModeRef.current;
        const strokes = allStrokeCyclesRef.current;
        const currentIndex = selectedStrokeIndexRef.current ?? 0;
        const nextIndex = currentIndex + 1;
        const inBounds = nextIndex < strokes.length;
        const shouldAdvance = hasData && currentPerStrokeMode && strokes.length > 0 && inBounds;
        if (!hasData) return;
        // Per-stroke mode only: move by one stroke; no action in normal mode
        if (shouldAdvance) {
          const stroke = strokes[nextIndex];
          const strokeCenterTime = (stroke.start + stroke.end) / 2;
          const extent = timeExtentRef.current;
          const range = animationRangeRef.current;
          const t0 = extent?.[0] ?? strokes[0]?.start ?? 0;
          const animRange =
            range ?? (strokes.length > 0 ? strokes[strokes.length - 1].end - t0 : 1);
          const newProgress = Math.max(
            0,
            Math.min(100, ((strokeCenterTime - t0) / animRange) * 100)
          );
          setIsPlaying(false);
          setSelectedStrokeIndex(nextIndex);
          selectedStrokeIndexRef.current = nextIndex;
          setProgress(newProgress);
        }
        return;
      }
    };

    // Document capture so arrow keys are always received (chart SVG or any focus)
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [hasData, handlePlayPause, handleReset, handleProgressChange]);

  const topControls =
    sessionId && sessionId.length > 0 ? (
      <PowerAnalysisControls
        sessionId={sessionId}
        hideDataSourceToggle={hideDataSourceToggle}
        calibrationReady={calibrationReady || false}
        hasCalibration={hasCalibration}
        alpha={alpha || 0}
        method={method || null}
        leverMode={leverMode}
        showAdvanced={showAdvanced}
        leverSuggestion={leverSuggestion}
        onLeverModeToggle={handleLeverModeToggle}
        onShowAdvancedToggle={handleShowAdvancedToggle}
        onCalibrationComplete={handleCalibrationComplete}
        onCalibrationRemoved={handleCalibrationRemoved}
        onCalibratingChange={setIsCalibrating}
        containerWidth={width}
      />
    ) : null;

  // Report errors
  useEffect(() => {
    if (error) {
      reportError(new Error(error), {
        context: 'power_analysis_d3',
        sessionId: sessionId || 'unknown',
      });
    }
  }, [error, sessionId, reportError]);

  return (
    <div
      ref={outerRef}
      onMouseEnter={() => {
        isHoveredRef.current = true;
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
      }}
      className="h-full w-full"
    >
      <ChartContainer
        title={title}
        subtitle={hullSetFilterSubtitle(subtitle, hullFilter)}
        maximizable={true}
        loading={loading}
        error={error}
        controls={topControls}
        className={className}
      >
        <div className="flex flex-col h-full gap-4">
          {!loading && !error && hasData && (
            <>
              {/* Animation Controls - Fixed position above chart */}
              <PlayerControls
                isPlaying={isPlaying}
                progress={progress}
                speed={speed}
                hasData={hasData}
                windowRangeLabel={windowRangeLabel}
                onPlayPause={handlePlayPause}
                onReset={handleReset}
                onSpeedChange={handleSpeedChange}
                onProgressChange={handleProgressChange}
                onResetZoom={handleResetZoom}
                allStrokeCycles={allStrokeCycles}
                timeExtent={timeExtent}
                animationRange={animationRange}
                onStrokeClick={handleStrokeClick}
                perStrokeMode={perStrokeMode}
                onPerStrokeModeToggle={() => setPerStrokeMode((prev) => !prev)}
              />

              {/* No Data Message */}
              {!hasData && (
                <div className="mb-2 p-2 bg-yellow-50 text-yellow-800 text-sm rounded">
                  No power data available for this session
                </div>
              )}
            </>
          )}

          {/* SVG Container - Always render for refs */}
          <div
            ref={chartContainerRef}
            className="relative w-full h-[400px] sm:h-[500px] lg:h-[600px] overflow-hidden"
          >
            <svg ref={svgRef} className="w-full h-full" />

            {isCalibrating && (
              <div
                className="absolute inset-0 z-20 flex items-center justify-center bg-white/55"
                role="status"
                aria-live="polite"
                aria-busy="true"
              >
                <div className="flex items-center gap-2.5 rounded-md border border-gray-200 bg-white px-4 py-2.5 text-base font-medium text-gray-700 shadow-sm">
                  <Loader2 className="h-5 w-5 animate-spin shrink-0" aria-hidden />
                  Calibrating…
                </div>
              </div>
            )}

            {/* Tooltip */}
            <div ref={tooltipRef} className={CHART_TOOLTIP_CLASS} style={{ opacity: 0 }} />
          </div>

          {/* React legend — outside SVG so it is never torn down by D3 re-renders */}
          {hasData && visibleRowers.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-4 -mt-4 pb-2">
              {visibleRowers.map((key) => {
                const isHidden = hiddenRowerSet.has(key);
                const isNonFocused = focusedRowerKey !== null && focusedRowerKey !== key;
                const dimmed = isHidden || isNonFocused;
                const color = isNonFocused ? 'rgba(100, 100, 100, 0.25)' : getRowerColorByKey(key);
                const position = key.replace('Rower ', '');
                const rowerName =
                  nameMapping?.[position] || nameMapping?.[Number(position)] || null;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleLegendToggle(key)}
                    className="flex items-center gap-2 rounded px-1 transition-opacity duration-150 outline-none focus-visible:ring-2 focus-visible:ring-focus/15"
                    style={{ opacity: dimmed ? 0.3 : 1 }}
                    title={rowerName || 'Unassigned'}
                    aria-label={`Toggle ${key.replace('Rower ', 'R')}${rowerName ? ` (${rowerName})` : ''}, currently ${dimmed ? 'hidden' : 'visible'}`}
                  >
                    <span
                      className="inline-block rounded-full border-2 flex-shrink-0"
                      style={{
                        width: 12,
                        height: 12,
                        borderColor: color,
                        backgroundColor: '#fff',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      }}
                    />
                    <span
                      className="text-xs font-medium text-gray-700 select-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {key.replace('Rower ', 'R')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

export default PowerAnalysisD3;
